import { useAuth } from '@/lib/auth/auth';
import type { EmailPreferences, UpdateEmailPreferencesRequest } from '@/lib/api/apiTypes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const emailPreferenceKeys = {
  all: ['email-preferences'] as const,
  current: () => [...emailPreferenceKeys.all, 'current'] as const,
};

export function useEmailPreferences() {
  const session = useAuth((state) => state.session);
  const request = useAuth((state) => state.request);

  return useQuery({
    queryKey: emailPreferenceKeys.current(),
    enabled: Boolean(session),
    queryFn: () => request<EmailPreferences>('/me/email-preferences'),
  });
}

export function useUpdateEmailPreferences() {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateEmailPreferencesRequest) =>
      request<EmailPreferences>('/me/email-preferences', {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: emailPreferenceKeys.current() });

      const previous = queryClient.getQueryData<EmailPreferences>(emailPreferenceKeys.current());
      if (previous) {
        queryClient.setQueryData(emailPreferenceKeys.current(), applyEmailPreferencePatch(previous, payload));
      }

      return { previous };
    },
    onError: (_error, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(emailPreferenceKeys.current(), context.previous);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(emailPreferenceKeys.current(), data);
    },
  });
}

function applyEmailPreferencePatch(
  current: EmailPreferences,
  payload: UpdateEmailPreferencesRequest
): EmailPreferences {
  return {
    email_reminders: payload.email_reminders ?? current.email_reminders,
    email_digest: payload.email_digest ?? current.email_digest,
    email_marketing: payload.email_marketing ?? current.email_marketing,
    email_new_events: payload.email_new_events ?? current.email_new_events,
    email_friend_activity: payload.email_friend_activity ?? current.email_friend_activity,
    email_venue_updates: payload.email_venue_updates ?? current.email_venue_updates,
    email_product_updates: payload.email_product_updates ?? current.email_product_updates,
    email_unsubscribed_all:
      payload.email_unsubscribed_all ?? current.email_unsubscribed_all,
  };
}
